import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { MapContext } from "../../contexts/map";

import { FeatureCollection } from "geojson";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import * as turf from "@turf/turf";

type DrawEventHandler = (e: any, f?: FeatureCollection) => void;
const Noop = (e: any, f?: FeatureCollection) => {};

interface MapDrawerProps {
  options?: {
    displayControlsDefault?: boolean;
    controls?: {
      point?: boolean;
      line_string?: boolean;
      polygon?: boolean;
      trash?: boolean;
      combine_features?: boolean;
      uncombine_features?: boolean;
    };
  };
  value?: FeatureCollection | null;
  setValue?: React.Dispatch<FeatureCollection | null>;
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  onDrawActionable?: DrawEventHandler;
  onDrawCombine?: DrawEventHandler;
  onDrawCreate?: DrawEventHandler;
  onDrawDelete?: DrawEventHandler;
  onDrawModeChange?: DrawEventHandler;
  onDrawRender?: DrawEventHandler;
  onDrawSelectionChange?: DrawEventHandler;
  onDrawUncombine?: DrawEventHandler;
  onDrawUpdate?: DrawEventHandler;
}

const MapDrawer: React.FC<MapDrawerProps> = ({
  options = {},
  value,
  setValue,
  position,
  onDrawActionable = Noop,
  onDrawCombine = Noop,
  onDrawCreate = Noop,
  onDrawDelete = Noop,
  onDrawModeChange = Noop,
  onDrawRender = Noop,
  onDrawSelectionChange = Noop,
  onDrawUncombine = Noop,
  onDrawUpdate = Noop,
}) => {
  const { map, setMap } = useContext(MapContext);
  const [recenter, setRecenter] = useState(true);

  const draw = useMemo(
    () =>
      new MapboxDraw({
        ...options,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const withSetValue = useCallback(
    (handler: DrawEventHandler) => (e: any) => {
      const _features = { ...draw.getAll() };
      if (setValue) {
        setValue(_features);
      }
      handler(e, _features);
    },
    [draw, setValue]
  );

  useEffect(() => {
    if (map) {
      map.addControl(draw, position);
      value && draw.set(value);

      map.on("draw.actionable", (e) => onDrawActionable(e, draw.getAll()));
      map.on("draw.combine", withSetValue(onDrawCombine));
      map.on("draw.create", withSetValue(onDrawCreate));
      map.on("draw.delete", withSetValue(onDrawDelete));
      map.on("draw.modechange", (e) => onDrawModeChange(e, draw.getAll()));
      map.on("draw.render", (e) => onDrawRender(e, draw.getAll()));
      map.on("draw.selectionchange", (e) =>
        onDrawSelectionChange(e, draw.getAll())
      );
      map.on("draw.uncombine", withSetValue(onDrawUncombine));
      map.on("draw.update", withSetValue(onDrawUpdate));

      setMap(map);
    }
    return () => {
      if (map) {
        map.removeControl(draw);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      map &&
      draw &&
      value &&
      Array.isArray(value.features) &&
      value.features.length &&
      recenter
    ) {
      const [x1, y1, x2, y2] = turf.bbox(value);
      map.fitBounds([x1, y1, x2, y2], { padding: 20 });
      setRecenter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenter, value]);

  return <></>;
};

export default MapDrawer;
